import { snakeToCamel } from '../CaseUtils';

// Customer Search  (GET /deliveryops/customers/search)

/**
 * Transforms one customer search result row.
 *
 * API shape: { id, first_name, last_name, email, phone, primary_address }
 * After snakeToCamel: id, firstName, lastName, email, phone, primaryAddress
 */
export const CustomerSearchResultETL = (result) => snakeToCamel(result);

/**
 * Transforms the results array from a customer search response.
 */
export const CustomerSearchListETL = (results) =>
  results?.map(CustomerSearchResultETL) ?? [];

/**
 * Transforms the full customer search response envelope.
 * Returns { page, results } with camelCase result rows.
 */
export const CustomerSearchResponseETL = (response) => ({
  page: response?.page ?? 1,
  results: CustomerSearchListETL(response?.results),
});

// Customer Detail  (GET /deliveryops/customers/{customerId})

/**
 * Transforms the full customer profile response.
 *
 * API shape (top-level):
 * {
 *   id, first_name, last_name, email,
 *   phones:       [{ id, number }],
 *   addresses:    [{ id, address1, city, state, zip, is_default, is_active }],
 *   tags:         [{ id, name, business_id }],
 *   price_levels: [{ business_id, tier_level }],
 *   orders: { page, page_size, all_time_count, all_time_total, results: [...] },
 *   errors: [{ id, order_id, error_type, solution_type, cost, note, created }]
 * }
 *
 * snakeToCamel is applied recursively to all nested objects and arrays.
 * orders.results[] retains legacy fields and maps created_at_utc,
 * confirmed_at_utc and restaurant_timezone to createdAtUtc, confirmedAtUtc
 * and restaurantTimezone. Customer-error timestamps have no order-time contract.
 */
export const CustomerDetailETL = (response) => snakeToCamel(response);

// Order Receipt  (GET /deliveryops/orders/receipt?q=...)

/**
 * Transforms the full order receipt response.
 *
 * API shape:
 * {
 *   id, display_id, status, status_id, order_type, delivery_type,
 *   cancel_note, business_id,
 *   timeline: { placed_at, confirmed_at, picked_up_at, delivered_at, cancelled_at,
 *     placed_at_utc, confirmed_at_utc, picked_up_at_utc, delivered_at_utc, cancelled_at_utc },
 *   restaurant: { id, name, address, city, state, zip, phone, timezone },
 *   customer:   { id, first_name, last_name, phone },
 *   delivery_address: { address1, city, state, zip },
 *   financials: { subtotal, delivery_fee, service_fee, tax, adjustment,
 *     adjustment_note, coupon_discount, gift_certificate_discount,
 *     order_total, total_paid },
 *   payment: { type },
 *   driver: { id, first_name, last_name, phone } | null,
 *   delivery_notes, dispatch_notes,
 *   items: [{ id, name, quantity, unit_price, label, special_instructions,
 *     options: [{ id, question_text, answer_text, label, quantity, price,
 *       special_instructions }] }]
 * }
 *
 * snakeToCamel applied recursively to all nested objects and arrays.
 */
export const OrderReceiptETL = (response) => snakeToCamel(response);

// Order Search  (GET /deliveryops/orders/search)

/**
 * Transforms one order result row from an order search response.
 *
 * API shape: { id, display_id, status_id, status, order_type, delivery_type,
 *   subtotal, delivery_fee, service_fee, tax, adjustment, coupon_discount,
 *   total, created_at, confirmed_at, cancel_note, business_id,
 *   restaurant_id, restaurant_name, restaurant_timezone, created_at_utc, confirmed_at_utc }
 * UTC companions are ISO second-precision Z strings or null; recursive mapping
 * produces createdAtUtc/confirmedAtUtc, without modifying legacy values.
 */
export const OrderSearchResultETL = (result) => snakeToCamel(result);

/**
 * Transforms the full order search response envelope.
 * Returns { totalResults, totalPages, page, results } with camelCase rows.
 * Envelope keys are mapped explicitly to avoid snake_case collisions.
 */
export const OrderSearchResponseETL = (response) => ({
  totalResults: response?.total_results ?? 0,
  totalPages: response?.total_pages ?? 1,
  page: response?.page ?? 1,
  results: response?.results?.map(OrderSearchResultETL) ?? [],
});
